"use client";

import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { preprocessVideoFrame } from '@/utils/imagePreprocessor';
import { registerStream, unregisterStream, stopStream, stopAllStreams } from '@/utils/mediaManager';
import TurnOffCamera from '@/utils/cameraControl';

// Lightweight camera barcode scanner modal.
// Tries native BarcodeDetector, falls back to continuous canvas capture.
export default function BarcodeScannerModal({ onClose, onDetected, open = true }: { onClose: ()=>void; onDetected: (code: string)=>void; open?: boolean }){
  // If not open, don't render anything
  if (!open) return null;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const drawRafRef = useRef<number | null>(null);
  const codeReaderRef = useRef<any | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorRef = useRef<any | null>(null);
  // prevent multiple detections firing in rapid succession
  const detectedRef = useRef<boolean>(false);
  const closeCalledRef = useRef<number>(0);
  const powerOffCalledRef = useRef<number>(0);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [overlayBox, setOverlayBox] = useState<{left:number;top:number;width:number;height:number}|null>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const { toast } = useToast();
  const LAST_DEVICE_KEY = 'barcode_scanner_last_device';
  const toastShownRef = useRef<{ cameraError?: boolean; initError?: boolean }>({});
  // session token to invalidate pending startScan operations
  const scanSessionRef = useRef<number>(0);
  // flag set when user explicitly powers off camera to prevent re-attach
  const poweredOffRef = useRef<boolean>(false);
  // High-frequency scan loop control
  const scanIntervalRef = useRef<number | null>(null);
  const lastDetectionTimeRef = useRef<number>(0);
  // Debounce detection to avoid duplicate triggers
  const DEBOUNCE_MS = 500;
  const DIGITAL_ZOOM_X = 2;

  const submitManualCode = () => {
    const clean = (manualCode || '').replace(/\D/g, '').trim();
    if (!clean) {
      toast({ title: 'Ingresar código', description: 'Escribí un número válido para buscar.' });
      return;
    }
    if (detectedRef.current) return;
    detectedRef.current = true;
    try { stopScan(); } catch (_) {}
    try { onDetected(clean); } catch (_) {}
    try { closeAndStop(); } catch (_) {}
  };

  const startScan = async (deviceId?: string | null) => {
    // when starting a new scan, clear powered-off flag and bump session token
    poweredOffRef.current = false;
    // allow reopen: reset close/power refs and detection guard
    try { closeCalledRef.current = 0; } catch (_) {}
    try { powerOffCalledRef.current = 0; } catch (_) {}
    try { detectedRef.current = false; } catch (_) {}
    scanSessionRef.current += 1;
    const mySession = scanSessionRef.current;
    let stream: MediaStream | null = null;
    // reset toast flags on each start
    toastShownRef.current = {};
    // HARDENING: always stop any previously tracked streams to avoid collisions
    try {
      try { console.debug('[BarcodeScannerModal] startScan: forcing stopAllStreams() to avoid collisions'); } catch(_) {}
      stopAllStreams();
    } catch (e) { try { console.debug('[BarcodeScannerModal] stopAllStreams failed', e); } catch(_) {} }
      try {
        if (!navigator || !(navigator as any).mediaDevices || typeof (navigator as any).mediaDevices.getUserMedia !== 'function') {
          toast({ title: 'Cámara no disponible', description: 'El navegador no permite acceder a la cámara.' });
          setScanning(false);
          return;
        }
      // prefer deviceId if provided
      const strategies: Array<any> = [];
      if (deviceId) strategies.push({ video: { deviceId: { exact: deviceId } } });
      strategies.push({ video: { facingMode: 'environment' } });
      strategies.push({ video: true });
        let lastErr: any = null;
        for (const s of strategies) {
          try {
             
            stream = await navigator.mediaDevices.getUserMedia(s);
            console.debug('[BarcodeScannerModal] getUserMedia succeeded with constraints', s, { tracks: stream.getTracks().map(t => ({ kind: t.kind, label: t.label })) });
            // if session invalidated while awaiting getUserMedia, stop this stream and abort
            if (mySession !== scanSessionRef.current || poweredOffRef.current) {
              try { stream.getTracks().forEach(t => { try { t.stop(); } catch (_) {} }); } catch (_) {}
              stream = null;
              break;
            }
            break;
          } catch (e) {
            lastErr = e;
          }
        }
        if (!stream) {
          console.error('All getUserMedia strategies failed', lastErr);
          if (!toastShownRef.current.cameraError) {
            toast({ title: 'No se pudo iniciar la cámara', description: 'Revisá permisos o probá seleccionar otra cámara en el selector.' });
            toastShownRef.current.cameraError = true;
          }
          setScanning(false);
          return;
        }
          if (poweredOffRef.current) {
            // user powered off while starting - ensure we don't attach stream
            if (stream) {
              try { stream.getTracks().forEach(t => { try { t.stop(); } catch (_) {} }); } catch (_) {}
            }
            return;
          }
          if (videoRef.current) {
          try {
            videoRef.current.srcObject = stream;
              videoRef.current.onloadedmetadata = () => {
                try {
                  if (canvasRef.current) {
                    canvasRef.current.width = videoRef.current!.videoWidth || videoRef.current!.clientWidth || 640;
                    canvasRef.current.height = videoRef.current!.videoHeight || videoRef.current!.clientHeight || 480;
                    canvasRef.current.style.width = '100%';
                    canvasRef.current.style.height = 'auto';
                  }
                  console.debug('[BarcodeScannerModal] video onloadedmetadata', { readyState: videoRef.current?.readyState, videoWidth: videoRef.current?.videoWidth, videoHeight: videoRef.current?.videoHeight });
                } catch (e) {}
              };
            try { await videoRef.current.play(); } catch (e) { /* ignore play errors */ }
          } catch (e) {
            console.error('Error attaching stream to video element', e);
            if (!toastShownRef.current.initError) {
              toast({ title: 'Error de cámara', description: 'No se pudo inicializar la vista previa de la cámara.' });
              toastShownRef.current.initError = true;
            }
            setScanning(false);
            return;
          }
        }
          // log stream tracks and settings for diagnostics
          try {
            console.debug('[BarcodeScannerModal] stream tracks', stream.getTracks().map(t => ({ id: t.id, kind: t.kind, label: t.label, enabled: t.enabled })));
            try { console.debug('[BarcodeScannerModal] getSettings (first video track)', (stream.getVideoTracks()[0] as any)?.getSettings?.()); } catch(_){}
          } catch (e) { console.warn('[BarcodeScannerModal] stream diagnostics failed', e); }

        // lightweight draw loop for debug/preview (keeps canvas updated)
        // now with image preprocessing for improved barcode readability
        const startDrawLoop = () => {
          let frameCount = 0;
          const draw = () => {
            try {
              if (canvasRef.current && videoRef.current && videoRef.current.readyState >= 2) {
                try {
                  const w = videoRef.current.videoWidth || videoRef.current.clientWidth || 640;
                  const h = videoRef.current.videoHeight || videoRef.current.clientHeight || 480;
                  if (canvasRef.current.width !== w || canvasRef.current.height !== h) {
                    canvasRef.current.width = w;
                    canvasRef.current.height = h;
                  }
                  
                  // Apply image preprocessing for better barcode detection
                  preprocessVideoFrame(videoRef.current, canvasRef.current, {
                    contrast: 1.5,        // Enhanced contrast for better bar separation
                    brightness: 15,       // Slight brightness boost
                    saturation: 1.2,      // Subtle saturation for color distinction
                    blur: 0.5,            // Minimal blur for noise reduction
                    sharpen: 0.3,         // Slight sharpening for edges
                    gamma: 1.2,           // Gamma correction for better visibility
                    binarize: false,      // Keep color data for detector
                  });
                } catch (e) {
                  if (frameCount % 240 === 0) console.warn('[BarcodeScannerModal] drawImage/preprocessing failed', e);
                }
              }
              // periodic diagnostics about readyState and dimensions
              frameCount += 1;
              if (frameCount % 60 === 0 && videoRef.current) {
                console.debug('[BarcodeScannerModal] video readyState', videoRef.current.readyState, 'videoW/H', videoRef.current.videoWidth, videoRef.current.videoHeight);
              }
            } catch (e) {
              if ((frameCount || 0) % 240 === 0) console.warn('[BarcodeScannerModal] draw loop error', e);
            }
            rafRef.current = requestAnimationFrame(draw);
          };
          drawRafRef.current = requestAnimationFrame(draw);
        };
        startDrawLoop();

        try {
          if ('BarcodeDetector' in window) {
            const Native = (window as any).BarcodeDetector;
            const nativeDetector = new Native({ formats: ['ean_13', 'ean_8', 'code_128', 'qr_code'] });
            detectorRef.current = nativeDetector;
            console.debug('[BarcodeScannerModal] native BarcodeDetector available');
          } else {
            detectorRef.current = null;
          }
        } catch (e) {
          detectorRef.current = null;
        }

        streamRef.current = stream;
        // Try to set camera zoom to 2x when supported by device
        try {
          const vt = stream.getVideoTracks()[0];
          if (vt && typeof vt.applyConstraints === 'function') {
            try {
              const caps = typeof vt.getCapabilities === 'function' ? vt.getCapabilities() : ({} as any);
              const desiredZoom = DIGITAL_ZOOM_X;
              if (caps && typeof caps.zoom !== 'undefined') {
                const min = caps.zoom?.min ?? 1;
                const max = caps.zoom?.max ?? desiredZoom;
                const zoomTo = Math.max(min, Math.min(max, desiredZoom));
                // applyConstraints may reject on some browsers; guard with try/catch
                await vt.applyConstraints({ advanced: [{ zoom: zoomTo }] });
                console.debug('[BarcodeScannerModal] applied camera zoom', zoomTo, 'capabilities:', caps);
              } else {
                // no native zoom capability; nothing to do (could fallback to CSS scale if desired)
                console.debug('[BarcodeScannerModal] camera zoom not supported by capabilities', caps);
              }
            } catch (e) {
              console.debug('[BarcodeScannerModal] applyConstraints(zoom) failed', e);
            }
          }
        } catch (e) {
          console.debug('[BarcodeScannerModal] zoom setup failed', e);
        }
        // If the session changed after attaching, immediately stop to avoid re-attach/race
        if (mySession !== scanSessionRef.current || poweredOffRef.current) {
          try { if (streamRef.current) { streamRef.current.getTracks().forEach(t => { try { t.stop(); } catch (_) {} }); } } catch (_) {}
          streamRef.current = null;
          return;
        }
        try { registerStream(streamRef.current); } catch (_) {}
        setScanning(true);

        // If native BarcodeDetector available, use optimized detection with multiple strategies
        if (detectorRef.current && videoRef.current) {
          const optimizedDetectionLoop = async () => {
            try {
              // Debounce: 80ms minimum between detection cycles
              const now = Date.now();
              if (now - lastDetectionTimeRef.current < 80) return;
              lastDetectionTimeRef.current = now;

              let detections: any[] = [];

              // Strategy 1: Aggressive preprocessing + full canvas detection
              if (canvasRef.current && videoRef.current) {
                try {
                  preprocessVideoFrame(videoRef.current, canvasRef.current, {
                    contrast: 2.3,        // Aggressive contrast
                    brightness: 30,
                    saturation: 1.6,
                    blur: 0.15,           // Minimal blur
                    sharpen: 0.7,         // Strong sharpen
                    gamma: 1.4,
                    binarize: false,
                  });
                  try {
                    detections = await detectorRef.current.detect(canvasRef.current as any);
                  } catch (e) {
                    detections = await detectorRef.current.detect(videoRef.current as HTMLVideoElement);
                  }
                } catch (e) {
                  // Continue to next strategy
                }
              }

              // Strategy 2: ROI (Region of Interest) - center-focused detection
              if ((!detections || detections.length === 0) && canvasRef.current && videoRef.current) {
                try {
                  const w = canvasRef.current.width;
                  const h = canvasRef.current.height;
                  const roiX = Math.floor(w * 0.2);
                  const roiY = Math.floor(h * 0.2);
                  const roiW = Math.floor(w * 0.6);
                  const roiH = Math.floor(h * 0.6);

                  const roiCanvas = document.createElement('canvas');
                  roiCanvas.width = roiW;
                  roiCanvas.height = roiH;
                  const roiCtx = roiCanvas.getContext('2d');
                  if (roiCtx) {
                    roiCtx.drawImage(canvasRef.current, roiX, roiY, roiW, roiH, 0, 0, roiW, roiH);
                    detections = await detectorRef.current.detect(roiCanvas as any);
                  }
                } catch (e) {
                  // Continue
                }
              }

              // Strategy 3: Adaptive binarization + upscale
              if ((!detections || detections.length === 0) && canvasRef.current) {
                try {
                  const tmp = document.createElement('canvas');
                  tmp.width = canvasRef.current.width * 1.5;
                  tmp.height = canvasRef.current.height * 1.5;
                  const tctx = tmp.getContext('2d');
                  if (tctx) {
                    tctx.drawImage(canvasRef.current, 0, 0, tmp.width, tmp.height);
                    
                    // Adaptive threshold
                    const id = tctx.getImageData(0, 0, tmp.width, tmp.height);
                    let sum = 0, count = 0;
                    for (let i = 0; i < id.data.length; i += 4) {
                      sum += 0.299 * id.data[i] + 0.587 * id.data[i + 1] + 0.114 * id.data[i + 2];
                      count++;
                    }
                    const threshold = Math.round(sum / count);

                    // Binarize
                    for (let i = 0; i < id.data.length; i += 4) {
                      const lum = 0.299 * id.data[i] + 0.587 * id.data[i + 1] + 0.114 * id.data[i + 2];
                      const v = lum > threshold ? 255 : 0;
                      id.data[i] = id.data[i + 1] = id.data[i + 2] = v;
                    }
                    tctx.putImageData(id, 0, 0);
                    detections = await detectorRef.current.detect(tmp as any);
                  }
                } catch (e) {
                  // ignore
                }
              }

              // Strategy 4: Rotated detection for vertical barcodes
              if ((!detections || detections.length === 0) && canvasRef.current) {
                try {
                  const source = canvasRef.current;
                  const rotated = document.createElement('canvas');
                  rotated.width = source.height;
                  rotated.height = source.width;
                  const rctx = rotated.getContext('2d');
                  if (rctx) {
                    // 90deg rotation
                    rctx.save();
                    rctx.translate(rotated.width, 0);
                    rctx.rotate(Math.PI / 2);
                    rctx.drawImage(source, 0, 0);
                    rctx.restore();
                    detections = await detectorRef.current.detect(rotated as any);

                    // 270deg rotation if no detection yet
                    if (!detections || detections.length === 0) {
                      rctx.clearRect(0, 0, rotated.width, rotated.height);
                      rctx.save();
                      rctx.translate(0, rotated.height);
                      rctx.rotate(-Math.PI / 2);
                      rctx.drawImage(source, 0, 0);
                      rctx.restore();
                      detections = await detectorRef.current.detect(rotated as any);
                    }
                  }
                } catch (e) {
                  // ignore
                }
              }

              // Process detections
              if (detections && detections.length > 0) {
                const sorted = (detections as any[]).sort((a, b) => 
                  (b.confidence || b.score || 1) - (a.confidence || a.score || 1)
                );
                const d = sorted[0];
                const code = d.rawValue || d.raw_data || d.raw || null;

                if (code) {
                  // Update overlay
                  try {
                    const vbw = (videoRef.current!.videoWidth || 1);
                    const vbh = (videoRef.current!.videoHeight || 1);
                    if (d.boundingBox) {
                      const bx = d.boundingBox.x || d.boundingBox.left || 0;
                      const by = d.boundingBox.y || d.boundingBox.top || 0;
                      const bw = d.boundingBox.width || d.boundingBox.right - bx || 0;
                      const bh = d.boundingBox.height || d.boundingBox.bottom - by || 0;
                      setOverlayBox({ left: (bx / vbw) * 100, top: (by / vbh) * 100, width: (bw / vbw) * 100, height: (bh / vbh) * 100 });
                    }
                  } catch (e) { /* ignore */ }

                  // Trigger callback
                  if (!detectedRef.current) {
                    detectedRef.current = true;
                    try { stopScan(); } catch (_) {}
                    try { onDetected(String(code)); } catch (_) {}
                    try { closeAndStop(); } catch (_) {}
                  }
                  return;
                }
              } else {
                setOverlayBox(null);
              }
            } catch (e) {
              console.warn('[BARCODE] detection error', e);
            }
          };

          // Run at 12fps (83ms) for responsive scanning
          scanIntervalRef.current = window.setInterval(optimizedDetectionLoop, 83) as any;
          // Try immediate
          optimizedDetectionLoop();
        } else {
          // Fallback: ZXing
          try {
            const zx = await import('@zxing/browser');
            const codeReader = new zx.BrowserMultiFormatReader();
            codeReaderRef.current = codeReader;
            await codeReader.decodeFromVideoDevice(undefined, videoRef.current as HTMLVideoElement, (result: any, err: any) => {
              if (result && typeof result.getText === 'function') {
                try {
                  // Try to extract result points for overlay
                  const points = result.getResultPoints ? result.getResultPoints() : null;
                  if (points && points.length > 0 && videoRef.current) {
                    const xs = points.map((p:any)=>p.getX ? p.getX() : p.x);
                    const ys = points.map((p:any)=>p.getY ? p.getY() : p.y);
                    const minX = Math.min(...xs); const minY = Math.min(...ys);
                    const maxX = Math.max(...xs); const maxY = Math.max(...ys);
                    const vbw = (videoRef.current.videoWidth || 1);
                    const vbh = (videoRef.current.videoHeight || 1);
                    setOverlayBox({ left: (minX / vbw) * 100, top: (minY / vbh) * 100, width: ((maxX - minX) / vbw) * 100, height: ((maxY - minY) / vbh) * 100 });
                  }
                  try { stopScan(); } catch(_) {}
                  if (!detectedRef.current) {
                    detectedRef.current = true;
                    onDetected(String(result.getText()));
                  }
                } catch (e) { console.warn('onDetected error', e); }
                try { codeReader.reset(); } catch (_) {}
                closeAndStop();
              }
            });
          } catch (e) {
            console.warn('zxing import/decoder error', e);
            console.debug('[BarcodeScannerModal] ZXing fallback not available or failed to load', e?.message || e);
            // fallback to visual/manual capture if ZXing is not available
            if (videoRef.current && canvasRef.current) {
              const ctx = canvasRef.current.getContext('2d');
              canvasRef.current.width = videoRef.current.videoWidth || 640;
              canvasRef.current.height = videoRef.current.videoHeight || 480;
              if (ctx) ctx.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
            }
          }
        }
      } catch (e:any) {
        console.error('camera init error', e);
        setError(String(e?.message || e));
        if (!toastShownRef.current.cameraError) {
          toast({ title: 'Error cámara', description: 'No se pudo acceder a la cámara.' });
          toastShownRef.current.cameraError = true;
        }
      }
  };

  const stopScan = () => {
    console.log('[BARCODE] 🛑 stopScan START');
    setScanning(false);
    try { console.log('[BARCODE] streamRef ID before stop:', streamRef.current ? streamRef.current.id : 'NULL'); } catch (_) {}
    
    // CRITICAL: Kill all loops and detectors FIRST
    try { 
      if (scanIntervalRef.current !== null) {
        console.log('[BARCODE] Clearing scanInterval');
        clearInterval(scanIntervalRef.current as number);
        scanIntervalRef.current = null;
      }
    } catch (_) {}
    try { 
      if (rafRef.current) { 
        console.log('[BARCODE] Canceling rafRef animation frame');
        cancelAnimationFrame(rafRef.current); 
        rafRef.current = null; 
      } 
    } catch (_) {}
    try { 
      if (drawRafRef.current) { 
        console.log('[BARCODE] Canceling drawRafRef animation frame');
        cancelAnimationFrame(drawRafRef.current); 
        drawRafRef.current = null; 
      } 
    } catch (_) {}
    
    // Kill native BarcodeDetector if active
    try {
      if (detectorRef.current) {
        console.log('[BARCODE] Killing native BarcodeDetector');
        detectorRef.current = null;
      }
    } catch (_) {}
    
    // Reset and kill ZXing code reader COMPLETELY
    try {
      if (codeReaderRef.current) {
        console.log('[BARCODE] Resetting ZXing codeReader');
        try { 
          if (typeof codeReaderRef.current.reset === 'function') {
            codeReaderRef.current.reset();
            console.log('[BARCODE] codeReader.reset() executed');
          }
        } catch (e) { console.warn('[BARCODE] Error resetting codeReader:', e); }
        try { codeReaderRef.current = null; } catch (_) {}
      }
    } catch (_) {}
    
    // AGGRESSIVE video element cleanup
    try {
      if (videoRef.current) {
        console.log('[BARCODE] Cleaning video element');
        try { videoRef.current.muted = true; } catch (_) {}
        try { videoRef.current.pause(); console.log('[BARCODE] Video paused'); } catch (_) {}
        try { videoRef.current.srcObject = null; console.log('[BARCODE] srcObject set to null'); } catch (_) {}
        try { (videoRef.current as any).src = ''; } catch (_) {}
        try { videoRef.current.removeAttribute('src'); } catch (_) {}
        try { videoRef.current.load(); } catch (_) {}
      }
    } catch (_) {}
    
    // Stop all tracks from streamRef
    const stopStreamSafe = (s: MediaStream | null) => {
      try {
        if (!s) return;
        console.log('[BARCODE] Stopping stream:', s.id);
        try { 
          const tracks = s.getTracks();
          console.log('[BARCODE] Stream has', tracks.length, 'tracks');
          tracks.forEach((track, i) => { 
            console.log(`[BARCODE] Disabling track ${i} (${track.kind})`);
            try { track.enabled = false; } catch (_) {}
            try { track.stop(); console.log(`[BARCODE] Track ${i} stopped`); } catch (_) {} 
          }); 
        } catch (_) {}
      } catch (_) {}
    };
    
    // Stop stored stream reference
    try {
      if (streamRef.current) {
        console.log('[BARCODE] Stopping streamRef');
        stopStreamSafe(streamRef.current);
        try { stopStream(streamRef.current); console.log('[BARCODE] mediaManager.stopStream() called'); } catch (_) {}
        try { unregisterStream(streamRef.current); console.log('[BARCODE] mediaManager.unregisterStream() called'); } catch (_) {}
        streamRef.current = null;
      }
    } catch (_) {}
    
    // Stop any stream attached to video element
    try {
      const s = videoRef.current ? (videoRef.current.srcObject as MediaStream | null) : null;
      if (s) {
        console.log('[BARCODE] Stopping video element srcObject stream');
        stopStreamSafe(s);
        try { stopStream(s); } catch (_) {}
        try { unregisterStream(s); } catch (_) {}
      }
    } catch (_) {}
    
    // Global mediaManager cleanup
    try { 
      if (typeof (window as any).__mediaManager?.stopAllStreams === 'function') {
        console.log('[BARCODE] Calling mediaManager.stopAllStreams()');
        (window as any).__mediaManager.stopAllStreams(); 
      }
    } catch (_) {}
    try { if (typeof (window as any).stopAllStreams === 'function') (window as any).stopAllStreams(); } catch (_) {}
    
    console.log('[BARCODE] 🛑 stopScan COMPLETE');
  };

  // Utility: stop all tracks attached to a video element (or window-level fallback)
  const stopAllStreams = (videoElement?: HTMLVideoElement | null) => {
    try {
      // Stop streamRef explicitly first
      try {
        if (streamRef.current) {
          try { streamRef.current.getTracks().forEach(t => { try { t.stop(); t.enabled = false; } catch (_) {} }); } catch (_) {}
          try { unregisterStream(streamRef.current); } catch (_) {}
          try { stopStream(streamRef.current); } catch (_) {}
          streamRef.current = null;
        }
      } catch (_) {}

      const v = videoElement || videoRef.current;
      if (v && (v.srcObject as MediaStream | null)) {
        const stream = v.srcObject as MediaStream;
        const tracks = stream.getTracks();
        tracks.forEach(track => { try { track.stop(); track.enabled = false; } catch (_) {} });
        try { v.srcObject = null; } catch (_) {}
        try { v.pause(); } catch (_) {}
        try { v.removeAttribute('src'); } catch (_) {}
        try { v.load(); } catch (_) {}
        return;
      }
    } catch (_) {}
    // fallback: stop any video elements on the page
    try {
      const vids = Array.from(document.querySelectorAll('video')) as HTMLVideoElement[];
      vids.forEach(v => {
        try {
          const s = v.srcObject as MediaStream | null;
          if (s) {
            s.getTracks().forEach(t => { try { t.stop(); t.enabled = false; } catch (_) {} });
            try { v.srcObject = null; } catch (_) {}
            try { v.pause(); } catch (_) {}
            try { v.removeAttribute('src'); } catch (_) {}
            try { v.load(); } catch (_) {}
          }
        } catch (_) {}
      });
    } catch (_) {}
    // Ensure mediaManager global cleanup
    try { if (typeof (window as any).__mediaManager?.stopAllStreams === 'function') (window as any).__mediaManager.stopAllStreams(); } catch (_) {}
  };

  // Atomic helper: reset decoder and force-stop a media stream (stops tracks, unregisters and clears video)
  const forceStopStream = (s?: MediaStream | null) => {
    console.log('[BARCODE] ⚛️ forceStopStream START');
    try {
      // Ensure ZXing / code reader is reset first to release any native locks
      if (codeReaderRef.current && typeof codeReaderRef.current.reset === 'function') {
        console.log('[BARCODE] Resetting codeReader in forceStopStream');
        try { codeReaderRef.current.reset(); } catch (_) {}
        try { codeReaderRef.current = null; } catch (_) {}
      }
    } catch (_) {}
    try {
      const streamToStop = s || streamRef.current || (videoRef.current ? (videoRef.current.srcObject as MediaStream | null) : null);
      if (!streamToStop) {
        console.log('[BARCODE] No stream to stop, trying mediaManager');
        // As a last resort, ask the global media manager to stop everything
        try { if (typeof (window as any).__mediaManager?.stopAllStreams === 'function') (window as any).__mediaManager.stopAllStreams(); } catch (_) {}
        return;
      }
      console.log('[BARCODE] Stopping stream:', streamToStop.id);
      try { streamToStop.getTracks().forEach(t => { try { t.stop(); t.enabled = false; } catch (_) {} }); } catch (_) {}
      try { unregisterStream(streamToStop); } catch (_) {}
      try { stopStream(streamToStop); } catch (_) {}
      try { streamRef.current = null; } catch (_) {}
    } catch (_) {}
    try {
      if (videoRef.current) {
        console.log('[BARCODE] Clearing video in forceStopStream');
        try { videoRef.current.pause(); } catch (_) {}
        try { videoRef.current.srcObject = null; } catch (_) {}
        try { videoRef.current.removeAttribute('src'); } catch (_) {}
        try { videoRef.current.load(); } catch (_) {}
      }
    } catch (_) {}
    console.log('[BARCODE] ⚛️ forceStopStream END');
  };

  // helper to reliably stop camera then notify parent to close modal
  const closeAndStop = () => {
    console.log('[BARCODE] 🚪 closeAndStop INITIATED');
    setIsCameraActive(false);
    
    // guard to prevent duplicated close operations
    try {
      if (closeCalledRef.current > 0) {
        console.log('[BARCODE] ⚠️ closeAndStop already called, skipping. count=', closeCalledRef.current);
        return;
      }
      closeCalledRef.current += 1;
    } catch (_) {}

    // CRITICAL ORDER: Stop scan FIRST (kills all loops and detectors)
    console.log('[BARCODE] → Calling stopScan()');
    try { stopScan(); } catch (e) { console.warn('[BARCODE] stopScan error:', e); }
    
    // Then force stop any remaining streams
    console.log('[BARCODE] → Calling forceStopStream()');
    try { forceStopStream(); } catch (e) { console.warn('[BARCODE] forceStopStream error:', e); }
    
    // Power off camera (disable tracks)
    console.log('[BARCODE] → Calling powerOffCamera()');
    try { powerOffCamera(); } catch (e) { console.warn('[BARCODE] powerOffCamera error:', e); }
    
    // System-level camera shutdown
    console.log('[BARCODE] → Calling TurnOffCamera()');
    try { TurnOffCamera(); } catch (e) { console.warn('[BARCODE] TurnOffCamera error:', e); }
    
    // Ensure ALL video elements on entire page are killed
    try {
      const videoElements = document.querySelectorAll('video');
      console.log('[BARCODE] Found', videoElements.length, 'video elements on page');
      videoElements.forEach((v, i) => {
        console.log(`[BARCODE] Killing video element ${i}`);
        try { v.muted = true; } catch (_) {}
        try { v.pause(); } catch (_) {}
        try { (v.srcObject as MediaStream | null) = null; } catch (_) {}
        try { (v as any).src = ''; } catch (_) {}
      });
    } catch (_) {}
    
    // Final global mediaManager nuke
    console.log('[BARCODE] → Final mediaManager nuke');
    try { if (typeof (window as any).__mediaManager?.stopAllStreams === 'function') (window as any).__mediaManager.stopAllStreams(); } catch (_) {}
    
    // Wait a bit then check final state
    try {
      setTimeout(() => {
        const state = (window as any).__mediaManager ? (window as any).__mediaManager.listStreams() : 'none';
        console.log('[BARCODE] ✅ closeAndStop COMPLETE - final mediaManager state:', state);
      }, 100);
    } catch (_) {}
    
    // Notify parent to close modal
    console.log('[BARCODE] → Calling onClose()');
    try { onClose(); } catch (e) { console.warn('[BARCODE] onClose error:', e); }
    
    console.log('[BARCODE] 🚪 closeAndStop FINISHED');
  };

  // Power-off helper: stop all streams but keep modal open (user can restart)
  const powerOffCamera = () => {
    console.log('[BARCODE] 💡 powerOffCamera START');
    setIsCameraActive(false);
    // mark powered off to prevent pending/ongoing startScan from re-attaching
    poweredOffRef.current = true;
    // bump session to invalidate any pending start attempts
    scanSessionRef.current += 1;
    
    try {
      // Cancel any animation frames
      if (rafRef.current) { 
        console.log('[BARCODE] Canceling raf');
        cancelAnimationFrame(rafRef.current); 
        rafRef.current = null; 
      }
      if (drawRafRef.current) { 
        console.log('[BARCODE] Canceling drawRaf');
        cancelAnimationFrame(drawRafRef.current); 
        drawRafRef.current = null; 
      }
    } catch (_) {}
    
    // Disable all video tracks FIRST
    try {
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks();
        console.log('[BARCODE] Disabling', tracks.length, 'tracks');
        tracks.forEach((t, i) => { 
          try { 
            t.enabled = false; 
            console.log(`[BARCODE] Track ${i} disabled (${t.kind})`);
          } catch (_) {}
        });
      }
    } catch (_) {}
    
    // Kill code reader
    try { 
      if (codeReaderRef.current && typeof codeReaderRef.current.reset === 'function') { 
        console.log('[BARCODE] Resetting codeReader');
        codeReaderRef.current.reset();
        codeReaderRef.current = null;
      } 
    } catch (_) {}
    
    // Kill detector
    try { 
      console.log('[BARCODE] Killing detector');
      detectorRef.current = null; 
    } catch (_) {}
    
    // Stop and unregister the current stream
    try {
      if (streamRef.current) {
        console.log('[BARCODE] Stopping streamRef');
        try { stopStream(streamRef.current); console.log('[BARCODE] stopStream called'); } catch (_) {}
        try { unregisterStream(streamRef.current); console.log('[BARCODE] unregisterStream called'); } catch (_) {}
        try { 
          streamRef.current.getTracks().forEach(t => { 
            try { t.stop(); console.log('[BARCODE] Track stopped'); } catch (_) {} 
          }); 
        } catch (_) {}
        streamRef.current = null;
      }
    } catch (_) {}
    
    // Clear the video element
    try {
      if (videoRef.current) {
        console.log('[BARCODE] Clearing video element');
        try { videoRef.current.muted = true; } catch (_) {}
        try { videoRef.current.pause(); } catch (_) {}
        try { (videoRef.current.srcObject as MediaStream | null) = null; } catch (_) {}
        try { (videoRef.current as any).src = ''; } catch (_) {}
      }
    } catch (_) {}
    
    // Global stops
    console.log('[BARCODE] Global cleanup');
    try { stopAllStreams(); } catch (_) {}
    try { TurnOffCamera(); } catch (_) {}
    
    console.log('[BARCODE] 💡 powerOffCamera END');
  };

  useEffect(() => {
    let isMounted = true;

    const doInit = async () => {
      // Try to prefill selectedDeviceId from localStorage
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          const saved = window.localStorage.getItem(LAST_DEVICE_KEY);
          if (saved) setSelectedDeviceId(saved);
        }
      } catch (_) {}
      
      await enumerateDevices();
      
      // Auto-start camera with first available device or saved device
      if (isMounted) {
        try {
          const savedDevice = typeof window !== 'undefined' && window.localStorage 
            ? window.localStorage.getItem(LAST_DEVICE_KEY)
            : null;
          await new Promise(resolve => setTimeout(resolve, 100)); // brief delay for device enumeration
          if (isMounted) {
            if (savedDevice) {
              setSelectedDeviceId(savedDevice);
            }
          }
        } catch (_) {}
      }
    };

    doInit();

    // Cleanup on unmount: ensure complete stream shutdown
    return () => {
      isMounted = false;
      try {
        powerOffCamera(); // This already handles all cleanup
      } catch (_) {}
    };
  }, []);

  // Auto-start camera when selectedDeviceId is set
  useEffect(() => {
    if (selectedDeviceId && !scanning && !isCameraActive) {
      const autoStart = async () => {
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(LAST_DEVICE_KEY, selectedDeviceId);
          }
        } catch (_) {}
        setScanning(true);
        setIsCameraActive(true);
        await startScan(selectedDeviceId);
      };
      autoStart();
    }
  }, [selectedDeviceId]);

  const enumerateDevices = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;
      let all = await navigator.mediaDevices.enumerateDevices();
      // If labels are empty (no permissions granted), try to request permission briefly to get device labels
      const vids = all.filter(d => d.kind === 'videoinput');
      if (vids.length === 0 || vids.every(v => !v.label)) {
        try {
          // Ask for camera permission to surface device labels (won't attach to video element here)
          const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
          try { tempStream.getTracks().forEach(t => t.stop()); } catch (_) {}
          all = await navigator.mediaDevices.enumerateDevices();
        } catch (_) {
          // ignore permission errors
        }
      }
      const vids2 = all.filter(d => d.kind === 'videoinput');
      console.debug('[BarcodeScannerModal] available video devices', vids2.map(v=>({ deviceId: v.deviceId, label: v.label })));
      setDevices(vids2);
      if (vids2.length > 0 && !selectedDeviceId) setSelectedDeviceId(vids2[0].deviceId || null);
    } catch (e) {
      // ignore
    }
  };

  return (
    <div className="fixed inset-0 z-[10020] flex bg-gradient-to-br from-background via-background to-primary/5">
      {/* Full screen camera area */}
      <div className="flex-1 flex flex-col relative">
        {/* Video + Canvas */}
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-black/60">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            style={{ transform: `scale(${DIGITAL_ZOOM_X})`, transformOrigin: 'center center' }}
            playsInline
            muted
            autoPlay
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          {/* Alignment overlay - Minibid style */}
          <div aria-hidden className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-3/4 h-3/4 border-3 border-primary rounded-xl" style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.4) inset, 0 0 30px rgba(249, 115, 22, 0.3)' }} />
          </div>
        </div>

        {/* Top controls - close only */}
        <div className="absolute right-4 top-4 z-20">
          <button
            onClick={() => { closeAndStop(); }}
            className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold bg-background/85 text-foreground border border-primary/25 backdrop-blur hover:bg-background transition-all active:scale-95"
          >
            Cerrar
          </button>
        </div>

        {/* Bottom controls - device selector + status */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background via-background/95 to-transparent p-4 z-10 shadow-2xl shadow-black/20">
          <div className="max-w-lg mx-auto space-y-4">
            {/* Status indicator - Minibid pill style */}
            <div className="flex items-center justify-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-background/80 border border-primary/20 px-4 py-2 backdrop-blur-sm">
                <div className={`w-2.5 h-2.5 rounded-full transition-all ${isCameraActive ? 'bg-green-500 animate-pulse shadow-lg shadow-green-500/50' : 'bg-muted-foreground'}`} />
                <span className="text-xs font-medium text-foreground">
                  {isCameraActive ? 'Cámara activa' : 'Cámara inactiva'}
                </span>
              </div>
            </div>

            {/* Device selector - Minibid style */}
            {devices.length > 0 && (
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Seleccionar cámara:</label>
                <div className="flex gap-2 items-end">
                  <select 
                    value={selectedDeviceId || ''} 
                    onChange={(e) => { 
                      if (!isCameraActive) {
                        setSelectedDeviceId(e.target.value || null);
                      }
                    }} 
                    disabled={isCameraActive}
                    className="flex-1 border-2 border-primary/30 rounded-lg px-3 py-2.5 text-xs font-medium bg-card text-foreground disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  >
                    <option value="">Seleccionar cámara...</option>
                    {devices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Cámara ${devices.indexOf(d) + 1}`}</option>)}
                  </select>
                  <button
                    onClick={() => { enumerateDevices(); }} 
                    disabled={isCameraActive}
                    className="inline-flex items-center justify-center rounded-lg px-3 py-2.5 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                  >
                    Actualizar
                  </button>
                </div>
              </div>
            )}

            {/* Manual numeric input */}
            <div className="space-y-2 rounded-lg border border-primary/20 bg-background/80 p-3 backdrop-blur-sm">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Introducir número manualmente</label>
              <div className="flex items-center gap-2">
                <Input
                  value={manualCode}
                  onChange={(e) => setManualCode((e.target.value || '').replace(/\D/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitManualCode();
                    }
                  }}
                  placeholder="Ej: 7791234567890"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  enterKeyHint="done"
                  className="h-10 rounded-full border-primary/30 focus-visible:ring-primary/30"
                />
                <button
                  onClick={submitManualCode}
                  className="inline-flex h-10 items-center justify-center rounded-full px-4 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all active:scale-95"
                >
                  Ingresar
                </button>
              </div>
            </div>

            {/* Error display - Minibid error style */}
            {error && (
              <div className="rounded-lg border border-error/30 bg-error/10 p-3">
                <p className="text-xs font-medium text-error">
                  ⚠️ {error}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
